import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { successSVG, tickSVG, crossSVG } from '../../lib/allCustomSVGs';
import MessageBanner from '../../components/messageBanner/MessageBanner';
import verify_email_link_api_call from '../../lib/api/settings/communicationSettings/verify_email_link_api_call';
import { submitLogToBugsnag } from '../../lib/api/log';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState<boolean>(true);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const hasProcessedRef = useRef<boolean>(false);

  useEffect(() => {
    if (hasProcessedRef.current) {
      return;
    }

    const token = searchParams.get('token');
    const channelId = searchParams.get('channel_id');

    if (!token || !channelId) {
      setError(t('InvalidVerificationLink'));
      setIsVerifying(false);
      hasProcessedRef.current = true;
      return;
    }

    const verifyEmail = async () => {
      try {
        setIsVerifying(true);
        const response = await verify_email_link_api_call(channelId, token);

        if (response && response.success) {
          setIsVerified(true);
        } else {
          if (response && response.message) {
            setError(response.message);
          } else {
            setError(t('EmailVerificationFailed'));
          }
        }
      } catch (err: any) {
        console.error('Error verifying email:', err);
        submitLogToBugsnag(
          'error',
          `Email verification error: ${err?.message ?? err}`
        );
        setError(t('EmailVerificationError'));
      } finally {
        setIsVerifying(false);
        hasProcessedRef.current = true;
        setSearchParams({}, { replace: true });
      }
    };

    verifyEmail();
  }, []);

  if (isVerifying) {
    return (
      <MessageBanner
        title={t('VerifyingEmail')}
        subTitle={t('VerifyingEmail')}
        icon={successSVG}
        btnTitle={t('Ok')}
        onSelect={() => {}}
        success={true}
        isLoading={true}
      />
    );
  }

  if (error) {
    return (
      <MessageBanner
        title={t('VerificationError')}
        subTitle={error}
        icon={crossSVG}
        btnTitle={t('ReturnToDashboard')}
        onSelect={() => {
          navigate('/');
        }}
        success={false}
      />
    );
  }

  if (isVerified) {
    return (
      <MessageBanner
        title={t('EmailVerifySuccessMainText')}
        subTitle={t('EmailVerifySuccessSubText')}
        icon={tickSVG}
        btnTitle={t('ReturnToDashboard')}
        onSelect={() => {
          navigate('/');
        }}
        success={true}
      />
    );
  }

  return null;
};

export default VerifyEmail;
